import { useEffect, useRef, type PointerEvent, type RefObject } from 'react';

import {
  useBoardDrawingStore,
  useBoardElementRectStore,
  useBoardSelectionStore,
  useWallParticipantsStore,
  useWallContentStore,
  useWorkspaceUiStore,
  type WallBoard,
} from '@walls/store';
import { getCardPermissions } from '../../cards/hooks/useCardPermissions';
import { isCardVisibleForParticipantFilter } from '../../cards/utils/cardVisibility';
import { getShapePermissions } from '../../shapes/hooks/useShapePermissions';
import { getSelectableElements } from '../utils/boardElements';
import { buildSelectionRect, rectsIntersect } from '../utils/selectionGeometry';

export function useBoardMarqueeSelection(
  board: WallBoard,
  viewportRef: RefObject<HTMLElement | null>
) {
  const cards = useWallContentStore((state) => state.cards);
  const measuredRects = useBoardElementRectStore((state) => state.rects);
  const shapes = useWallContentStore((state) => state.shapes);
  const localShapes = useBoardDrawingStore((state) => state.localShapes);
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);
  const visibleParticipantIds = useWorkspaceUiStore((state) => state.visibleParticipantIds);
  const zoom = useWorkspaceUiStore((state) => state.zoom);
  const isSelecting = useBoardSelectionStore((state) => state.isSelecting);
  const selectionStart = useBoardSelectionStore((state) => state.selectionStart);
  const setCurrentElement = useBoardSelectionStore((state) => state.setCurrentElement);
  const setSelectedShape = useBoardDrawingStore((state) => state.setSelectedShape);
  const setIsSelecting = useBoardSelectionStore((state) => state.setIsSelecting);
  const setSelectedIds = useBoardSelectionStore((state) => state.setSelectedIds);
  const setSelectionArea = useBoardSelectionStore((state) => state.setSelectionArea);
  const setSelectionAreaViewport = useBoardSelectionStore(
    (state) => state.setSelectionAreaViewport
  );
  const setSelectionStart = useBoardSelectionStore((state) => state.setSelectionStart);
  const animationFrameRef = useRef<number | null>(null);
  const isSelectingRef = useRef(false);
  const pendingSelectionAreaRef = useRef<DOMRectInit | null>(null);
  const pendingSelectionAreaViewportRef = useRef<DOMRectInit | null>(null);
  const selectionStartViewportRef = useRef<{ x: number; y: number } | null>(null);
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    isSelectingRef.current = isSelecting;
    selectionStartRef.current = selectionStart;
  }, [isSelecting, selectionStart]);

  useEffect(
    () => () => {
      if (animationFrameRef.current != null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    },
    []
  );

  return {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      if (selectedTool !== 'pointer' || isBoardElementEvent(event)) {
        return;
      }

      const point = getBoardPoint(event, viewportRef.current, zoom);
      const viewportPoint = getViewportPoint(event, viewportRef.current);
      setCurrentElement(null);
      setSelectedShape(null);
      setIsSelecting(true);
      setSelectedIds([]);
      setSelectionArea(null);
      setSelectionAreaViewport(null);
      setSelectionStart(point);
      isSelectingRef.current = true;
      selectionStartRef.current = point;
      selectionStartViewportRef.current = viewportPoint;
      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => {
      const currentSelectionStart = selectionStartRef.current;

      if (!isSelectingRef.current || !currentSelectionStart) {
        return;
      }

      const point = getBoardPoint(event, viewportRef.current, zoom);
      const currentSelectionStartViewport = selectionStartViewportRef.current;
      const viewportPoint = getViewportPoint(event, viewportRef.current);

      pendingSelectionAreaRef.current = buildSelectionRect(currentSelectionStart, point);

      if (currentSelectionStartViewport) {
        pendingSelectionAreaViewportRef.current = buildSelectionRect(
          currentSelectionStartViewport,
          viewportPoint
        );
      }

      scheduleSelectionAreaUpdate();
      event.preventDefault();
    },
    onPointerUp: (event: PointerEvent<HTMLElement>) => {
      const currentSelectionStart = selectionStartRef.current;

      if (!isSelectingRef.current || !currentSelectionStart) {
        return;
      }

      flushSelectionAreaUpdate();
      const point = getBoardPoint(event, viewportRef.current, zoom);
      const selectionRect = buildSelectionRect(currentSelectionStart, point);
      const selectedElements = getSelectableElements(
        cards,
        [...shapes, ...localShapes],
        measuredRects
      ).filter(
        (element) =>
          rectsIntersect(selectionRect, element.rect) &&
          (!element.card ||
            (getCardPermissions(board, element.card, currentParticipantId).canSelect &&
              isCardVisibleForParticipantFilter(element.card, visibleParticipantIds))) &&
          (!element.shape ||
            getShapePermissions(board, element.shape, currentParticipantId).canSelect)
      );

      setSelectedIds(selectedElements.map((element) => element.id));
      setSelectedShape(getSingleSelectedShapeId(selectedElements));
      setSelectionArea(null);
      setSelectionAreaViewport(null);
      setSelectionStart(null);
      setIsSelecting(false);
      isSelectingRef.current = false;
      selectionStartRef.current = null;
      selectionStartViewportRef.current = null;

      if (selectedElements.length === 1) {
        setCurrentElement({ id: selectedElements[0].id, type: selectedElements[0].type });
      }

      event.currentTarget.releasePointerCapture(event.pointerId);
      event.preventDefault();
    },
    onPointerLeave: () => {
      if (isSelectingRef.current) {
        flushSelectionAreaUpdate();
        setSelectionArea(null);
        setSelectionAreaViewport(null);
        setSelectionStart(null);
        setIsSelecting(false);
        isSelectingRef.current = false;
        selectionStartRef.current = null;
        selectionStartViewportRef.current = null;
      }
    },
  };

  function scheduleSelectionAreaUpdate() {
    if (animationFrameRef.current != null) {
      return;
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = null;
      flushSelectionAreaUpdate();
    });
  }

  function flushSelectionAreaUpdate() {
    if (animationFrameRef.current != null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (pendingSelectionAreaRef.current) {
      setSelectionArea(pendingSelectionAreaRef.current);
      pendingSelectionAreaRef.current = null;
    }

    if (pendingSelectionAreaViewportRef.current) {
      setSelectionAreaViewport(pendingSelectionAreaViewportRef.current);
      pendingSelectionAreaViewportRef.current = null;
    }
  }
}

function getSingleSelectedShapeId(
  selectedElements: Array<{ id: string; type: string }>
) {
  return selectedElements.length === 1 &&
    ['shape', 'line', 'text'].includes(selectedElements[0].type)
    ? selectedElements[0].id
    : null;
}

function getBoardPoint(
  event: PointerEvent<HTMLElement>,
  viewport: HTMLElement | null,
  zoom: number
) {
  if (!viewport) {
    return { x: 0, y: 0 };
  }

  const rect = viewport.getBoundingClientRect();

  return {
    x: viewport.scrollLeft + (event.clientX - rect.left) / zoom,
    y: viewport.scrollTop + (event.clientY - rect.top) / zoom,
  };
}

function isBoardElementEvent(event: PointerEvent<HTMLElement>) {
  return event.target instanceof Element && Boolean(event.target.closest('[data-element-id]'));
}

function getViewportPoint(event: PointerEvent<HTMLElement>, viewport: HTMLElement | null) {
  if (!viewport) {
    return { x: 0, y: 0 };
  }

  const rect = viewport.getBoundingClientRect();

  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  };
}
